import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';
import DashboardLayout from '../../components/layout/DashboardLayout';
import AdminSidebar from '../../components/sidebar/AdminSidebar';
import ApprovalWorkflowDrawer from '../../components/dashboard/ApprovalWorkflowDrawer';
import './PendingRegistrationsPage.css';

interface KycDocument {
  _id: string;
  documentType: string;
  fileName: string;
  originalFileName: string;
  fileSize: number;
  mimeType: string;
  uploadedAt: string;
  fileUrl: string;
}

interface PendingKycSubmission {
  user: {
    _id: string;
    name: string;
    email: string;
    companyName: string;
  };
  documents: KycDocument[];
  submittedAt: string;
}

const PendingRegistrationsPage: React.FC = () => {
  const [pendingSubmissions, setPendingSubmissions] = useState<PendingKycSubmission[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedReviewItem, setSelectedReviewItem] = useState<{
    document: KycDocument;
    user: PendingKycSubmission['user'];
  } | null>(null);

  useEffect(() => {
    fetchPendingKycSubmissions();
  }, []);

  const fetchPendingKycSubmissions = async () => {
    try {
      const response = await adminApi.getPendingKycSubmissions();
      setPendingSubmissions(response.data);
    } catch (error) {
      console.error('Failed to fetch pending KYC submissions:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveDocument = async (documentId: string, userId: string) => {
    try {
      await adminApi.approveDocument(documentId);
      // Update the local state to reflect the approval
      setPendingSubmissions(submissions =>
        submissions.map(submission => {
          if (submission.user._id === userId) {
            return {
              ...submission,
              documents: submission.documents.filter(doc => doc._id !== documentId)
            };
          }
          return submission;
        }).filter(submission => submission.documents.length > 0) // Remove users with no pending documents
      );
      alert('Document approved successfully!');
    } catch (error) {
      console.error('Failed to approve document:', error);
      alert('Failed to approve document. Please try again.');
    }
  };

  const handleRejectDocument = async (documentId: string, userId: string, reason: string) => {
    try {
      await adminApi.rejectDocument(documentId, reason);
      // Update the local state to reflect the rejection
      setPendingSubmissions(submissions =>
        submissions.map(submission => {
          if (submission.user._id === userId) {
            return {
              ...submission,
              documents: submission.documents.filter(doc => doc._id !== documentId)
            };
          }
          return submission;
        }).filter(submission => submission.documents.length > 0) // Remove users with no pending documents
      );
      alert('Document rejected successfully.');
    } catch (error: any) {
      console.error('Failed to reject document:', error);

      // Show specific error message from backend if available
      if (error.response?.data?.message) {
        alert(`Failed to reject document: ${error.response.data.message}`);
      } else if (error.response?.data?.errors) {
        // Handle validation errors array
        const validationErrors = error.response.data.errors;
        if (Array.isArray(validationErrors) && validationErrors.length > 0) {
          alert(`Failed to reject document: ${validationErrors[0].message}`);
        } else {
          alert('Failed to reject document. Please check your input and try again.');
        }
      } else {
        alert('Failed to reject document. Please try again.');
      }
    }
  };

  const handleViewDocument = (fileUrl: string) => {
    window.open(fileUrl, '_blank');
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getDocumentTypeLabel = (type: string) => {
    const labels: { [key: string]: string } = {
      bvn: 'BVN',
      cac: 'CAC Certificate',
      proofOfAddress: 'Proof of Address',
      tin: 'TIN Certificate',
      directorInfo: 'Director Information'
    };
    return labels[type] || type;
  };

  return (
    <DashboardLayout sidebar={<AdminSidebar />}>
      <div className="pending-registrations-page">
        <div className="page-header">
          <h2>Pending KYC Submissions</h2>
          <p>Review and approve KYC documents for user registrations</p>
        </div>

        <div className="kyc-submissions-container">
          {pendingSubmissions.map((submission) => (
            <div key={submission.user._id} className="kyc-submission-card">
              <div className="user-info">
                <h3>{submission.user.name}</h3>
                <p className="user-email">{submission.user.email}</p>
                <p className="user-company">{submission.user.companyName}</p>
                <p className="submitted-date">
                  Submitted: {new Date(submission.submittedAt).toLocaleDateString()}
                </p>
              </div>

              <div className="documents-list">
                <h4>Documents ({submission.documents.length})</h4>
                {submission.documents.map((document) => (
                  <div key={document._id} className="document-item">
                    <div className="document-info">
                      <div className="document-type">
                        {getDocumentTypeLabel(document.documentType)}
                      </div>
                      <div className="document-details">
                        <span className="file-name">{document.originalFileName}</span>
                        <span className="file-size">({formatFileSize(document.fileSize)})</span>
                        <span className="upload-date">
                          {new Date(document.uploadedAt).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="document-actions">
                      <button
                        onClick={() => handleViewDocument(document.fileUrl)}
                        className="view-btn"
                      >
                        View
                      </button>
                      <button
                        onClick={() =>
                          setSelectedReviewItem({
                            document,
                            user: submission.user,
                          })
                        }
                        className="approve-btn"
                      >
                        Review in Drawer
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {loading ? (
          <div className="loading">
            <p>Loading pending KYC submissions...</p>
          </div>
        ) : pendingSubmissions.length === 0 ? (
          <div className="no-pending">
            <p>No pending KYC submissions at this time.</p>
          </div>
        ) : null}

        <ApprovalWorkflowDrawer
          isOpen={Boolean(selectedReviewItem)}
          title="KYC Registration Review"
          subtitle="Resolve pending registration documents with an auditable decision trail."
          onClose={() => setSelectedReviewItem(null)}
          contextItems={
            selectedReviewItem
              ? [
                  { label: 'Applicant', value: selectedReviewItem.user.name },
                  { label: 'Email', value: selectedReviewItem.user.email },
                  {
                    label: 'Document',
                    value: getDocumentTypeLabel(selectedReviewItem.document.documentType),
                  },
                  {
                    label: 'File',
                    value: selectedReviewItem.document.originalFileName,
                  },
                ]
              : []
          }
          onApprove={
            selectedReviewItem
              ? () =>
                  handleApproveDocument(
                    selectedReviewItem.document._id,
                    selectedReviewItem.user._id
                  )
              : undefined
          }
          onReject={
            selectedReviewItem
              ? (reason) =>
                  handleRejectDocument(
                    selectedReviewItem.document._id,
                    selectedReviewItem.user._id,
                    reason
                  )
              : undefined
          }
          rejectionLabel="KYC Rejection Reason"
          rejectionPlaceholder="Explain missing details or compliance failure for this registration."
        />
      </div>
    </DashboardLayout>
  );
};

export default PendingRegistrationsPage;
